import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronRight,
  ChevronLeft,
  ThermometerSun,
  Home,
  Building2,
  Building,
  Flame,
  Droplets,
  Zap,
  CheckCircle2,
  CalendarClock,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { toast } from "sonner";

const QUESTIONS = [
  {
    key: "building_type",
    title: "Welcher Gebäudetyp soll beheizt werden?",
    options: [
      { value: "Einfamilienhaus", icon: Home },
      { value: "Doppelhaushälfte", icon: Building },
      { value: "Mehrfamilienhaus", icon: Building2 },
      { value: "Gewerbe", icon: Building2 },
    ],
  },
  {
    key: "current_heating",
    title: "Womit heizen Sie aktuell?",
    options: [
      { value: "Ölheizung", icon: Droplets },
      { value: "Gasheizung", icon: Flame },
      { value: "Strom / Nachtspeicher", icon: Zap },
      { value: "Andere / Neubau", icon: Home },
    ],
  },
  {
    key: "living_area",
    title: "Wie groß ist die zu beheizende Wohnfläche?",
    options: [
      { value: "Bis 100 m²" },
      { value: "100 – 150 m²" },
      { value: "150 – 200 m²" },
      { value: "Über 200 m²" },
    ],
  },
  {
    key: "ownership",
    title: "Sind Sie Eigentümer der Immobilie?",
    options: [
      { value: "Ja, Eigentümer" },
      { value: "Nein, Mieter" },
      { value: "Geplanter Kauf / Neubau" },
    ],
  },
  {
    key: "timeframe",
    title: "Wann möchten Sie umsetzen?",
    options: [
      { value: "So schnell wie möglich", icon: CalendarClock },
      { value: "In 1 – 3 Monaten", icon: CalendarClock },
      { value: "In 3 – 6 Monaten", icon: CalendarClock },
      { value: "Nur informieren", icon: CalendarClock },
    ],
  },
];

const TOTAL_STEPS = QUESTIONS.length + 1; // + contact form

export default function Funnel() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [dir, setDir] = useState(1);
  const [answers, setAnswers] = useState({});
  const [contact, setContact] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    address: "",
    postal_code: "",
    city: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const isFormStep = step === QUESTIONS.length;
  const progress = Math.round(((done ? TOTAL_STEPS : step) / TOTAL_STEPS) * 100);

  const next = () => {
    setDir(1);
    setStep((s) => Math.min(s + 1, TOTAL_STEPS - 1));
  };
  const back = () => {
    setDir(-1);
    setStep((s) => Math.max(s - 1, 0));
  };

  const selectOption = (key, value) => {
    setAnswers((a) => ({ ...a, [key]: value }));
    setTimeout(next, 220);
  };

  const formValid =
    contact.first_name && contact.last_name && contact.email && contact.phone && contact.postal_code;

  const submit = async () => {
    if (!formValid) {
      toast.error("Bitte füllen Sie alle Pflichtfelder aus.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/leads", { ...contact, ...answers });
      setDone(true);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const variants = {
    enter: (d) => ({ opacity: 0, x: d > 0 ? 28 : -28 }),
    center: { opacity: 1, x: 0 },
    exit: (d) => ({ opacity: 0, x: d > 0 ? -28 : 28 }),
  };

  return (
    <div className="min-h-screen bg-bone flex flex-col">
      <Seo title="Angebot anfordern | Wärmekraft" description="Fordern Sie Ihr kostenloses Wärmepumpen-Angebot an." path="/funnel" noindex />
      {/* top bar */}
      <div className="px-6 py-5 border-b border-[#D9DBE0] bg-white/70 backdrop-blur-md sticky top-0 z-20">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="funnel-logo">
            <img src="/waermekraft-logo.png" alt="Wärmekraft" className="h-8 w-auto" />
          </Link>
          <span className="text-sm font-semibold text-[#9AA0AC]" data-testid="funnel-progress-label">
            {done ? "Fertig" : `Schritt ${step + 1} von ${TOTAL_STEPS}`}
          </span>
        </div>
      </div>

      <div className="flex-1 px-6 py-10 flex items-start justify-center">
        <div className="w-full max-w-2xl">
          {/* progress bar */}
          <div className="h-2 w-full rounded-full bg-[#D9DBE0] overflow-hidden mb-8">
            <motion.div
              className="h-full bg-brand"
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.4 }}
              data-testid="quiz-progress-bar"
            />
          </div>

          <div className="bg-white rounded-2xl p-8 md:p-12 shadow-[0_8px_32px_rgba(15,41,30,0.08)] border border-[#D9DBE0]">
            <AnimatePresence mode="wait" custom={dir}>
              {done ? (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center py-6"
                  data-testid="funnel-success"
                >
                  <div className="mx-auto h-16 w-16 rounded-full bg-brand/10 flex items-center justify-center">
                    <CheckCircle2 className="h-9 w-9 text-brand" />
                  </div>
                  <h2 className="mt-6 font-display font-bold text-2xl md:text-3xl text-forest">
                    Vielen Dank, {contact.first_name}!
                  </h2>
                  <p className="mt-4 text-[#5A6172] leading-relaxed max-w-md mx-auto">
                    Ihre Anfrage ist eingegangen. Eine Bestätigung wurde an{" "}
                    <span className="font-semibold text-forest">{contact.email}</span> gesendet.
                    Ein Fachpartner meldet sich innerhalb von 24 Stunden bei Ihnen.
                  </p>
                  <Link
                    to="/"
                    data-testid="success-home-btn"
                    className="mt-8 inline-flex items-center gap-2 rounded-full bg-forest px-7 py-3.5 text-sm font-bold text-white hover:bg-brand transition-colors"
                  >
                    Zurück zur Startseite
                  </Link>
                </motion.div>
              ) : isFormStep ? (
                <motion.div
                  key="form"
                  custom={dir}
                  variants={variants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{ duration: 0.3 }}
                >
                  <h2 className="font-display font-bold text-2xl md:text-3xl text-forest tracking-tight">
                    Fast geschafft! Ihre Kontaktdaten
                  </h2>
                  <p className="mt-2 text-sm text-[#9AA0AC]">
                    Damit wir Ihnen Ihr persönliches Festpreis-Angebot zusenden können.
                  </p>
                  <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <Field label="Vorname *" id="first_name" value={contact.first_name} onChange={(v) => setContact({ ...contact, first_name: v })} />
                    <Field label="Nachname *" id="last_name" value={contact.last_name} onChange={(v) => setContact({ ...contact, last_name: v })} />
                    <Field label="E-Mail *" id="email" type="email" value={contact.email} onChange={(v) => setContact({ ...contact, email: v })} />
                    <Field label="Telefon *" id="phone" value={contact.phone} onChange={(v) => setContact({ ...contact, phone: v })} />
                    <Field label="Straße & Hausnr." id="address" value={contact.address} onChange={(v) => setContact({ ...contact, address: v })} />
                    <div className="grid grid-cols-2 gap-3">
                      <Field label="PLZ *" id="postal_code" value={contact.postal_code} onChange={(v) => setContact({ ...contact, postal_code: v })} />
                      <Field label="Ort" id="city" value={contact.city} onChange={(v) => setContact({ ...contact, city: v })} />
                    </div>
                  </div>
                  <div className="mt-5">
                    <Label className="text-sm font-semibold text-forest">Nachricht (optional)</Label>
                    <Textarea
                      data-testid="lead-message"
                      className="mt-1.5"
                      rows={3}
                      value={contact.message}
                      onChange={(e) => setContact({ ...contact, message: e.target.value })}
                      placeholder="Haben Sie noch Fragen oder Anmerkungen?"
                    />
                  </div>
                  <div className="mt-8 flex items-center justify-between">
                    <button onClick={back} data-testid="form-back-btn" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#9AA0AC] hover:text-forest transition-colors">
                      <ChevronLeft className="h-4 w-4" /> Zurück
                    </button>
                    <button
                      onClick={submit}
                      disabled={submitting}
                      data-testid="lead-submit"
                      className="inline-flex items-center gap-2 rounded-full bg-terracotta px-8 py-3.5 text-sm font-bold text-white hover:bg-terracotta-hover transition-all disabled:opacity-60"
                    >
                      {submitting ? "Wird gesendet..." : "Angebot anfordern"}
                      {!submitting && <ChevronRight className="h-4 w-4" />}
                    </button>
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key={step}
                  custom={dir}
                  variants={variants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{ duration: 0.3 }}
                >
                  <h2 className="font-display font-bold text-2xl md:text-3xl text-forest tracking-tight">
                    {QUESTIONS[step].title}
                  </h2>
                  <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {QUESTIONS[step].options.map((opt) => {
                      const Icon = opt.icon;
                      const active = answers[QUESTIONS[step].key] === opt.value;
                      return (
                        <button
                          key={opt.value}
                          onClick={() => selectOption(QUESTIONS[step].key, opt.value)}
                          data-testid={`quiz-option-${opt.value}`}
                          className={`flex items-center gap-3 rounded-xl border p-5 text-left transition-all hover:-translate-y-1 ${
                            active
                              ? "border-brand bg-brand/5 ring-2 ring-brand/30"
                              : "border-[#D9DBE0] hover:border-brand hover:bg-bone"
                          }`}
                        >
                          {Icon && (
                            <span className="shrink-0 h-10 w-10 rounded-lg bg-brand/10 flex items-center justify-center">
                              <Icon className="h-5 w-5 text-brand" />
                            </span>
                          )}
                          <span className="font-semibold text-forest">{opt.value}</span>
                        </button>
                      );
                    })}
                  </div>
                  <div className="mt-8 flex items-center justify-between">
                    {step > 0 ? (
                      <button onClick={back} data-testid="quiz-back-btn" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#9AA0AC] hover:text-forest transition-colors">
                        <ChevronLeft className="h-4 w-4" /> Zurück
                      </button>
                    ) : (
                      <Link to="/" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#9AA0AC] hover:text-forest transition-colors">
                        <ChevronLeft className="h-4 w-4" /> Abbrechen
                      </Link>
                    )}
                    {answers[QUESTIONS[step].key] && (
                      <button onClick={next} data-testid="quiz-next-btn" className="inline-flex items-center gap-2 rounded-full bg-forest px-6 py-3 text-sm font-bold text-white hover:bg-brand transition-colors">
                        Weiter <ChevronRight className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, id, value, onChange, type = "text" }) {
  return (
    <div>
      <Label htmlFor={id} className="text-sm font-semibold text-forest">{label}</Label>
      <Input
        id={id}
        type={type}
        data-testid={`lead-${id}`}
        className="mt-1.5"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}
